import React from "react";
import { ApiError, cockpit, getTenantContext, type ConversationListItem } from "@/api";
import { Button, Dialog } from "@/ds";
import { useNotifications } from "@/notifications/notification-host";
import type { PushToast } from "@/notifications/toast-queue";
import { pathOf } from "@/router";
import { useSession } from "@/session";
import { useT } from "../i18n";
import { requestNotAssignedRefusal } from "./request-not-assigned";
import { requestTrashRefusal, requestTrashRefusalKey } from "./request-trash";
import { undoDoneRefusal, undoDoneRefusalKey } from "./request-done-undo";

type BulkAction = "trash" | "done" | "undoDone" | "restore";
type SelectedRequest = { id: string; ref: string; label: string; version: number };
type Outcome = SelectedRequest & { success: boolean; reason?: string };

export function hasBulkVersion(row: ConversationListItem): boolean {
  return Number.isSafeInteger(row.target_request_version) && row.target_request_version! >= 0;
}

export function InboxSelectionCheckbox({ label, checked, mixed = false, disabled = false, onChange }: {
  label: string; checked: boolean; mixed?: boolean; disabled?: boolean; onChange: (checked: boolean) => void;
}) {
  return <input type="checkbox" className="inbox-row-selector" aria-label={label}
    checked={checked} disabled={disabled}
    ref={(element) => { if (element) element.indeterminate = mixed && !checked; }}
    onChange={(event) => onChange(event.target.checked)} />;
}

/** Batch only the existing commands. Every call owns its ordinary transaction and refusals. */
async function perform(action: BulkAction, item: SelectedRequest) {
  switch (action) {
    case "trash": return cockpit.trashRequest(item.id, item.version);
    case "done": return cockpit.closeRequest(item.id, undefined, item.version);
    case "undoDone": return cockpit.undoDone(item.id, item.version);
    case "restore": return cockpit.restoreRequest(item.id, item.version);
  }
}

export function useInboxBulkActions({ rows, scope, trash, disabled, pushToast, reload, onTrashed }: {
  rows: ConversationListItem[];
  scope: string;
  trash: boolean;
  disabled: boolean;
  pushToast: PushToast;
  reload: () => Promise<void>;
  onTrashed: (requestId: string) => void;
}) {
  const t = useT();
  const { user } = useSession();
  const notifications = useNotifications();
  const identity = `${user?.id}:${user?.tenantId}`;
  const selectionScope = `${identity}:${scope}`;
  const [selection, setSelection] = React.useState<{ scope: string; items: SelectedRequest[] }>({
    scope: selectionScope, items: [],
  });
  const [confirmation, setConfirmation] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [report, setReport] = React.useState<{ identity: string; action: BulkAction; outcomes: Outcome[] } | null>(null);
  const mounted = React.useRef(true);
  const running = React.useRef(false);
  const current = React.useRef({ identity, selectionScope, reload });
  current.current = { identity, selectionScope, reload };
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => { setConfirmation(null); }, [selectionScope]);

  const visibleIds = new Set(rows.map((row) => row.target_request_id));
  const selected = selection.scope === selectionScope
    ? selection.items.filter((item) => visibleIds.has(item.id)) : [];
  const selectedIds = selected.map((item) => item.id);
  // Prune old-page/hidden rows permanently, so returning to a view cannot resurrect selection.
  React.useEffect(() => {
    setSelection((old) => {
      const items = old.scope === selectionScope ? old.items.filter((item) => visibleIds.has(item.id)) : [];
      return old.scope === selectionScope && items.length === old.items.length
        ? old : { scope: selectionScope, items };
    });
  }, [selectionScope, rows.map((row) => row.target_request_id).join(",")]);

  const selectRows = (candidates: ConversationListItem[], checked: boolean) => {
    if (disabled || running.current) return;
    const items = new Map(selected.map((item) => [item.id, item]));
    for (const row of candidates) {
      if (!hasBulkVersion(row) || !visibleIds.has(row.target_request_id)) continue;
      if (checked) {
        items.set(row.target_request_id, {
          id: row.target_request_id, ref: String(row.target_request_no ?? row.target_request_id),
          label: `${row.target_reference ?? `#${row.target_request_no ?? row.target_request_id}`} · ${row.subject || t("common.noSubject")}`,
          version: row.target_request_version!,
        });
      } else items.delete(row.target_request_id);
    }
    setSelection({ scope: selectionScope, items: [...items.values()] });
  };

  const refusalReason = (error: unknown) => {
    if (requestNotAssignedRefusal(error)) return t("inbox.bulk.notAssigned");
    const trashReason = requestTrashRefusal(error);
    if (trashReason) return t(requestTrashRefusalKey(trashReason));
    const undoReason = undoDoneRefusal(error);
    if (undoReason) return t(undoDoneRefusalKey(undoReason));
    if (error instanceof ApiError) {
      if ((error.data as { code?: string })?.code === "REQUEST_VERSION_CHANGED") return t("ws.markDoneStale");
      if (error.status === 401 || error.status === 403) return t("inbox.bulk.noPermission");
      if (error.status === 404) return t("inbox.bulk.unavailable");
      if (error.status >= 500) return t("inbox.bulk.unknownOutcome");
      return error.message;
    }
    return t("inbox.bulk.unknownOutcome");
  };

  const run = async (action: BulkAction, items = selected, requiredContext = getTenantContext(), fromUndo = false) => {
    if (!items.length || notifications.isDisposed()) return;
    if (running.current) {
      if (fromUndo) pushToast({ tone: "info", title: t("inbox.bulk.running"), undo: {
        label: t("toast.undo"), run: async () => { await run(action, items, requiredContext, true); },
      } });
      return;
    }
    if ((!mounted.current && !fromUndo) || current.current.identity !== identity || getTenantContext() !== requiredContext) {
      pushToast({ tone: "warning", title: t("inbox.bulk.contextChanged") });
      return;
    }
    running.current = true;
    if (mounted.current) { setBusy(true); setConfirmation(null); }
    const startedScope = current.current.selectionScope;
    const outcomes: Outcome[] = [];
    try {
      for (const item of items) {
        // An Undo may outlive Inbox, but never the session that offered it.
        if (notifications.isDisposed()
            || (!fromUndo && (!mounted.current || current.current.selectionScope !== startedScope))
            || getTenantContext() !== requiredContext) {
          outcomes.push({ ...item, success: false, reason: t("inbox.bulk.contextChanged") });
          continue;
        }
        try {
          const result = await perform(action, item);
          outcomes.push({ ...item, version: result.version, success: true });
          if (action === "trash" && mounted.current && !notifications.isDisposed()
              && current.current.identity === identity && current.current.selectionScope === startedScope
              && getTenantContext() === requiredContext) onTrashed(item.id);
        } catch (error) {
          // No retry: a lost response may hide a committed operation. The result says so.
          outcomes.push({ ...item, success: false, reason: refusalReason(error) });
        }
      }
      if (notifications.isDisposed() || current.current.identity !== identity || getTenantContext() !== requiredContext) return;
      const successes = outcomes.filter((item) => item.success);
      const failures = outcomes.filter((item) => !item.success);
      if (mounted.current) {
        setReport({ identity, action, outcomes });
        if (current.current.selectionScope === startedScope) {
          setSelection((old) => {
            const remaining = new Map((old.scope === startedScope ? old.items : []).map((item) => [item.id, item]));
            successes.forEach((item) => remaining.delete(item.id));
            failures.forEach((item) => remaining.set(item.id, item));
            return { scope: startedScope, items: [...remaining.values()] };
          });
        }
      }
      pushToast({
        tone: failures.length ? "warning" : "success",
        title: t(`inbox.bulk.${action}`),
        msg: <>
          {t("inbox.bulk.result", { success: successes.length, failed: failures.length })}
          {!mounted.current && failures.length > 0 && <ul>{failures.map((item) => <li key={item.id}>{item.label}: {item.reason}</li>)}</ul>}
        </>,
        ...(action === "trash" && successes.length ? { undo: {
          label: t("toast.undo"),
          // The notification host disposes actions on session/business changes. Undo also
          // remains usable after leaving Inbox, like the single-request trash action.
          run: async () => { await run("restore", successes, requiredContext, true); },
        } } : {}),
      });
      // The command is settled. A slow read must not hold Undo or the next selection busy.
      if (mounted.current) void current.current.reload();
    } finally {
      running.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const actions: BulkAction[] = trash ? ["restore"] : ["done", "undoDone", "trash"];
  const shownReport = report?.identity === identity ? report : null;
  return {
    busy,
    selectedIds,
    selectRows,
    toolbar: <>
      {/* #2103: no bar while nothing is selected; the table's own checkboxes start a selection.
          The count is still announced, and the bar follows the list so it never moves a row. */}
      <span className="sr-only" role="status" aria-live="polite">{t("inbox.bulk.selected", { n: selected.length })}</span>
      {(selected.length > 0 || busy) && <div className="inbox-bulk-bar" role="group" aria-label={t("inbox.bulk.label")} aria-busy={busy}>
        <span>{t("inbox.bulk.selected", { n: selected.length })}</span>
        {selected.length > 0 && <>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setSelection({ scope: selectionScope, items: [] })}>
            {t("inbox.bulk.clear")}
          </Button>
          <div className="inbox-bulk-bar__actions">
            {actions.map((action) => <Button key={action} size="sm"
              variant={action === "trash" ? "danger-quiet" : "secondary"}
              disabled={disabled || busy}
              onClick={() => action === "trash" ? setConfirmation(selectionScope) : void run(action)}>
              {t(`inbox.bulk.${action}`)}
            </Button>)}
          </div>
        </>}
        {busy && <span role="status">{t("inbox.bulk.running")}</span>}
      </div>}
      {shownReport && <details className="inbox-bulk-report" open={shownReport.outcomes.some((item) => !item.success)}>
        <summary>{t(`inbox.bulk.${shownReport.action}`)}: {t("inbox.bulk.result", {
          success: shownReport.outcomes.filter((item) => item.success).length,
          failed: shownReport.outcomes.filter((item) => !item.success).length,
        })}</summary>
        <ul>{shownReport.outcomes.map((item) => <li key={item.id}>
          <a href={pathOf({ name: "mail", id: item.ref })}>{item.label}</a>
          <span>{item.success ? t("inbox.bulk.success") : item.reason}</span>
        </li>)}</ul>
      </details>}
      <Dialog open={confirmation === selectionScope && selected.length > 0}
        title={t("inbox.bulk.trashTitle", { n: selected.length })} tone="danger"
        onClose={() => setConfirmation(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmation(null)}>{t("common.cancel")}</Button>
          <Button variant="destructive" disabled={disabled || busy} onClick={() => void run("trash")}>
            {t("inbox.bulk.trashConfirm", { n: selected.length })}
          </Button>
        </>}>
        <p>{t("inbox.bulk.trashBody")}</p>
      </Dialog>
    </>,
  };
}
